import React, { Component } from 'react';
import { helpers, styled } from 'react-free-style';
import { connect } from 'react-redux';
import { NavLink, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import classnames from 'classnames';
import { notify } from 'react-notify-toast';

import {
  colors,
  elements,
  sizes,
  IconButton,
  EditButton,
  buttonTypes,
} from '@united-talent-agency/julius-frontend-components';
import { Spinner } from '@united-talent-agency/components';
import {
  loadProject,
  updateProject,
  deleteProject,
  createProjectNote,
  updateProjectNote,
  projectLoadRoles,
  updateRole,
  createRole,
  loadTalentNotes,
  loadLiteraryNotes,
  loadTouringNotes,
  loadPANotes,
  loadActs,
  getProjectCastings,
  loadPerson,
} from '@united-talent-agency/julius-frontend-store';

import Title from '../../../components/title/title';
import ExceptionHandler from '../../../components/exception-handler/exception-handler';
import ProjectNotFound from '../project-not-found';
import SectionError from '../../../components/section-error/section-error';
import SectionErrorBanner from '../../../components/section-error-banner/section-error-banner';
import CastingsView from './views/castings/castings';
import InformationView from './views/information/information';
import TouringInformationView from './views/touring-information/touring-information';
import TrackingsView from './views/trackings/trackings';
import ActivityView from './views/activity/activity';
import { AuspicesView } from './views/auspices/';
import TalentNotesView from './views/notes/talentNotes/talentNotes';
import LiteraryNotesView from './views/notes/literaryNotes/literaryNotes';
import TouringNotesView from './views/notes/touringNotes/touringNotes';
import IndieNotesView from './views/notes/indieNotes/indieNotes';
import PANotesView from './views/notes/paNotes/paNotes';
import ActsView from './views/notes/acts/acts';
import { downloadFile } from '../../../support/download';
import PrintManager from '../../../components/print-manager/print-manager';
import { defaultUrl } from '../../../support/urls';
import { hasAccess } from '../../../support/permissions';
import { productionArtsPositions } from '../../../support/items/production-arts-positions';
import cypressTags from '../../../support/cypressTags';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

const allProjectTypes = ['Feature', 'Television', 'Unscripted', 'Theatre', 'Fairs and Festivals'];
const standardTypes = ['Feature', 'Television', 'Unscripted', 'Theatre'];
const indieTypes = ['Feature', 'Unscripted'];
const touringTypes = ['Fairs and Festivals'];

const updates = {
  project: updateProject,
  note: updateProjectNote,
};

const creates = {
  note: createProjectNote,
};

const showPositionsTab = process.env.REACT_APP_SHOW_SEPARATE_POSITIONS_TAB === 'true';

const FairsAndFestivalsMenu = ({ project, styles }) => (
  <React.Fragment>
    <NavLink
      exact={true}
      to={`/project/${project._id}`}
      className={styles.menuItem}
      activeClassName={styles.activeItem}
    >
      General
    </NavLink>
    <NavLink to={`/project/${project._id}/acts`} activeClassName={styles.activeItem} className={styles.menuItem}>
      Confirmed Acts
    </NavLink>
    <NavLink
      to={`/project/${project._id}/touring-notes`}
      activeClassName={styles.activeItem}
      className={styles.menuItem}
    >
      Notes
    </NavLink>
    <NavLink to={`/project/${project._id}/activity`} className={styles.menuItem} activeClassName={styles.activeItem}>
      Activity
    </NavLink>
  </React.Fragment>
);

export const DefaultProjectMenu = ({ project, styles = {}, canSeeIndieNotes }) => (
  <React.Fragment>
    <NavLink
      exact={true}
      to={`/project/${project._id}`}
      className={styles.menuItem}
      activeClassName={styles.activeItem}
    >
      General
    </NavLink>
    {showPositionsTab && (
      <NavLink to={`/project/${project._id}/auspices`} className={styles.menuItem} activeClassName={styles.activeItem}>
        Auspices
      </NavLink>
    )}
    <NavLink to={`/project/${project._id}/castings`} className={styles.menuItem} activeClassName={styles.activeItem}>
      Castings
    </NavLink>
    <NavLink to={`/project/${project._id}/production-arts`} className={styles.menuItem} activeClassName={styles.activeItem}>
      Production Arts
    </NavLink>
    <NavLink to={`/project/${project._id}/trackings`} className={styles.menuItem} activeClassName={styles.activeItem}>
      Trackings
    </NavLink>
    {canSeeIndieNotes && (
      <NavLink
        to={`/project/${project._id}/indie-notes`}
        className={styles.menuItem}
        activeClassName={styles.activeItem}
      >
        Indie Notes
      </NavLink>
    )}
    <NavLink
      to={`/project/${project._id}/literary-notes`}
      className={styles.menuItem}
      activeClassName={styles.activeItem}
    >
      Literary Notes
    </NavLink>
    <NavLink
      to={`/project/${project._id}/talent-notes`}
      className={styles.menuItem}
      activeClassName={styles.activeItem}
    >
      Talent Notes
    </NavLink>
    <NavLink
      to={`/project/${project._id}/pa-notes`}
      className={styles.menuItem}
      activeClassName={styles.activeItem}
    >
      PA Notes
    </NavLink>
    <NavLink to={`/project/${project._id}/activity`} className={styles.menuItem} activeClassName={styles.activeItem}>
      Activity
    </NavLink>
  </React.Fragment>
);

/**
 * Returns a <SectionError /> element for a given sibling-endpoint failure,
 * or null if the loader succeeded. Rendered at the Route layer (rather than
 * inside each tab view) so individual tab components stay unmodified and the
 * fallback automatically slots into the layout below the shared tab menu.
 */
const sectionFallback = (viewProps, key, label) =>
  viewProps.sectionErrors?.[key] ? (
    <SectionError
      title={`Couldn't load ${label}`}
      onRetry={viewProps.onSectionRetry}
      isRetrying={viewProps.isRetrying}
    />
  ) : null;

const FairsAndFestivalsRoutes = ({ viewProps, acts, touringNotes, project }) => (
  <React.Fragment>
    <Route
      exact={true}
      path="/project/:id"
      render={routerProps => <TouringInformationView {...routerProps} {...viewProps} visibleForTypes={touringTypes} />}
    />
    <Route
      path="/project/:id/acts"
      render={routerProps =>
        sectionFallback(viewProps, 'acts', 'Confirmed Acts') ||
        (acts && <ActsView {...routerProps} {...viewProps} project={project} touringNotes={touringNotes} />)
      }
    />
    <Route
      path="/project/:id/touring-notes"
      render={routerProps =>
        sectionFallback(viewProps, 'touringNotes', 'Touring Notes') ||
        (touringNotes && (
          <TouringNotesView {...routerProps} {...viewProps} project={project} touringNotes={touringNotes} />
        ))
      }
    />
    <Route
      path="/project/:id/activity"
      render={routerProps => <ActivityView {...routerProps} {...viewProps} visibleForTypes={allProjectTypes} />}
    />
  </React.Fragment>
);

const DefaultRoutes = ({ viewProps, literaryNotes, project, talentNotes, paNotes, desk, canSeeIndieNotes, user, dispatch }) => {
  return (
    <React.Fragment>
      <Route
        exact={true}
        path="/project/:id"
        render={routerProps => <InformationView {...routerProps} {...viewProps} visibleForTypes={standardTypes} />}
      />
      <Route
        path="/project/:id/activity"
        render={routerProps => <ActivityView {...routerProps} {...viewProps} visibleForTypes={allProjectTypes} />}
      />
      {canSeeIndieNotes && (
        <Route
          path="/project/:id/indie-notes"
          render={routeProps =>
            // Indie notes share the Literary Notes loader on the parent, so a
            // literary-notes failure also disables Indie Notes.
            sectionFallback(viewProps, 'literaryNotes', 'Indie Notes') || (
              <IndieNotesView
                {...routeProps}
                {...viewProps}
                project={project}
                visibleForTypes={indieTypes}
                desk={desk}
                user={user}
              />
            )
          }
        />
      )}
      <Route
        path="/project/:id/literary-notes"
        render={routeProps =>
          sectionFallback(viewProps, 'literaryNotes', 'Literary Notes') ||
          // Spread viewProps so the notes view receives retryToken +
          // isRetrying from the parent — the view uses them to re-fetch on
          // the page-level Retry and to drive its own inline SectionError
          // button state when its own useEffect-level loader fails.
          (literaryNotes && (
            <LiteraryNotesView {...routeProps} {...viewProps} project={project} literaryNotes={literaryNotes} />
          ))
        }
      />
      <Route
        path="/project/:id/talent-notes"
        render={routeProps =>
          sectionFallback(viewProps, 'talentNotes', 'Talent Notes') ||
          (talentNotes && (
            <TalentNotesView {...routeProps} {...viewProps} project={project} talentNotes={talentNotes} />
          ))
        }
      />
      <Route
        path="/project/:id/pa-notes"
        render={routeProps =>
          sectionFallback(viewProps, 'paNotes', 'PA Notes') ||
          (paNotes && <PANotesView {...routeProps} {...viewProps} project={project} paNotes={paNotes} />)
        }
      />
      <Route
        path="/project/:id/trackings"
        render={routerProps => <TrackingsView {...routerProps} {...viewProps} visibleForTypes={standardTypes} />}
      />
      <Route
        path="/project/:id/castings"
        render={routerProps =>
          sectionFallback(viewProps, 'castings', 'Castings') || (
            <CastingsView {...routerProps} {...viewProps} visibleForTypes={standardTypes} />
          )
        }
      />
      <Route
        path="/project/:id/production-arts"
        render={routerProps =>
          // Production Arts reads roles via the same loader as Auspices.
          sectionFallback(viewProps, 'roles', 'Production Arts') || (
            <AuspicesView
              {...routerProps}
              {...viewProps}
              visibleForTypes={standardTypes}
              roleTabFilter="production_arts"
              roleTabValue="production_arts"
              positions={productionArtsPositions}
              title="Production Arts"
              onPersonSelected={async (person) => {
                const result = await dispatch(loadPerson(person._id || person.objectID));
                const vocations = result?.body?.productionArtsVocations || [];
                return vocations;
              }}
            />
          )
        }
      />
      {showPositionsTab && (
        <Route
          path="/project/:id/auspices"
          render={routerProps =>
            sectionFallback(viewProps, 'roles', 'Auspices') || (
              <AuspicesView
                {...routerProps}
                {...viewProps}
                visibleForTypes={standardTypes}
                roleTabFilter="auspice"
                roleTabValue="auspice"
              />
            )
          }
        />
      )}
    </React.Fragment>
  );
};

class Project extends Component {
  constructor(props) {
    super(props);
    this.state = {
      focused: 0,
      view: 'information',
      isEdit: false,
      isPrintManagerCollapsed: true,
      originals: {},
      changedItems: [],
      lastChangeFrom: null,
      // Case 1 — the whole project failed to load (deleted / 5xx / network).
      // When set, the component renders <ProjectNotFound /> in place instead
      // of the project chrome.
      projectLoadError: null,
      // True from mount (and from the start of every setForm) until Phase 1
      // resolves. Gates the render so we never flash stale content from the
      // redux `projects.project` slice — this is what happens when the user
      // navigates away from a 404'd project and then uses the browser's
      // Forward button: the Redux store still holds the previous project
      // (or a `{ message: 'no record' }` envelope), the component mounts
      // with projectLoadError = null, and before setForm resolves we'd
      // otherwise briefly render the old chrome + a toast + a content
      // loader. Starting in a "loading" state keeps the UI quiet until
      // Phase 1 tells us what to actually show.
      isLoadingProject: true,
      // Case 2 — one or more side endpoints failed. Keyed by loader name:
      // roles, castings, talentNotes, literaryNotes, touringNotes, paNotes, acts.
      // Each value is { status, message } so individual tabs can render
      // <SectionError /> while the rest of the page stays usable.
      sectionErrors: {},
      // True while a retry is in flight — disables the Retry button and
      // lets tabs render a "Retrying…" affordance.
      isRetrying: false,
      // Incrementing counter plumbed into tabs that run their own loaders
      // (Trackings, IndieNotes). Including it in their useEffect deps forces
      // them to re-fetch on retry.
      retryToken: 0,
      // Controls visibility of the page-level SectionErrorBanner. Reset to
      // false on every setForm() so a fresh batch of failures re-surfaces
      // the banner even if the user previously dismissed it.
      isBannerDismissed: false,
    };
    this.onChange = this.onChange.bind(this);
    this.retryAll = this.retryAll.bind(this);
    this._projectEntities = [
      'networks',
      'studios',
      'productionCompanies',
      'talentAgents',
      'literaryAgents',
      'pointAgents',
      'indieAgents',
      'productionArtsAgents',
      'contacts',
      'parentCompanies',
      'promoters',
      'repAgreements',
      'salesFinancing',
      'readBy',
    ];
  }

  componentDidMount() {
    const { dispatch, projectId, errorMsg } = this.props;
    errorMsg !== null && notify.show(errorMsg, 'error');
    if (projectId) {
      this.setForm(dispatch, projectId);
    } else {
      // No projectId means we're never going to call setForm, so release the
      // loading gate immediately — otherwise the spinner would stay up
      // forever waiting for a Phase 1 that never runs.
      this.setState({ isLoadingProject: false });
    }
  }

  async setForm(dispatch, projectId) {
    // Clear any stale error state so a retry (or a navigation to a different
    // project id) starts from a clean slate. If this load fails again, the
    // new error will overwrite below. Also reset banner dismissal so new
    // failures surface the banner again even if the user hid the previous one.
    // isLoadingProject is (re-)asserted so a Retry triggered from the Case 1
    // fallback also goes through the spinner path rather than briefly
    // showing the chrome built from stale redux data.
    this.setState({
      projectLoadError: null,
      sectionErrors: {},
      isBannerDismissed: false,
      isLoadingProject: true,
    });

    // ------------------------------------------------------------------
    // Phase 1 — gate on the project itself.
    //
    // Issue the project load alone and inspect the result before firing any
    // siblings. This avoids ~7 wasted API calls per visit to a deleted /
    // non-existent project, and lets the full-page fallback render as soon
    // as the project 404 is known (instead of blocking on the slowest
    // sibling response).
    //
    // Trade-off: the happy path pays an extra T(project) on the critical
    // path (sequential before the siblings) in exchange for no wasted calls
    // and a snappier 404 fallback. We're accepting that trade intentionally
    // — see the earlier discussion in #FILM-412.
    // ------------------------------------------------------------------
    const projectResult = await dispatch(loadProject(projectId));

    // Case 1 — hard failure on the project itself.
    // Trigger: HTTP non-ok (404 "no record", 5xx, network, parse error) OR a
    // 200 whose body is just a bare `{ message }` envelope with no _id
    // (defensive — the API shouldn't do that, but the current reducer would
    // happily park the envelope in `state.projects.project` and crash the
    // rest of the render).
    const projectEnvelope = projectResult?.body;
    const projectBodyLooksLikeError =
      projectEnvelope && projectEnvelope.message && !projectEnvelope._id;
    if (!projectResult?.ok || projectBodyLooksLikeError) {
      this.setState({
        projectLoadError: {
          // Distinguishes 404 from other failures for analytics/telemetry;
          // the user-facing copy is the same so the UX stays uniform.
          kind: projectResult?.status === 404 ? 'not_found' : 'load_failed',
          status: projectResult?.status,
          message: projectEnvelope?.message,
        },
        // Phase 1 has resolved (as a failure) — stop blocking render on the
        // spinner so <ProjectNotFound /> can take over.
        isLoadingProject: false,
      });
      return;
    }

    // Phase 1 succeeded — release the render gate so the normal chrome can
    // paint. Phase 2 runs below and populates sectionErrors; those render
    // as inline cards inside the already-visible chrome.
    this.setState({ isLoadingProject: false });

    // ------------------------------------------------------------------
    // Phase 2 — project exists, fetch the sibling data in parallel.
    //
    // Any failures here are classified into `sectionErrors` so each tab can
    // render its own inline SectionError card while the rest of the page
    // stays usable. Keys mirror the viewProps naming so the `sectionFallback`
    // helper near each Route picks them up without a mapping layer.
    // ------------------------------------------------------------------
    const [
      roleResult,
      castingsResult,
      talentNotesResult,
      literaryNotesResult,
      touringNotesResult,
      paNotesResult,
      actsResult,
    ] = await Promise.all([
      dispatch(projectLoadRoles(projectId)),
      dispatch(getProjectCastings(projectId)),
      dispatch(loadTalentNotes(projectId)),
      dispatch(loadLiteraryNotes(projectId)),
      dispatch(loadTouringNotes(projectId)),
      dispatch(loadPANotes(projectId)),
      dispatch(loadActs(projectId)),
    ]);

    const sectionErrors = {};
    if (!roleResult?.ok) sectionErrors.roles = { status: roleResult?.status };
    if (!castingsResult?.ok) sectionErrors.castings = { status: castingsResult?.status };
    if (!talentNotesResult?.ok) sectionErrors.talentNotes = { status: talentNotesResult?.status };
    if (!literaryNotesResult?.ok) sectionErrors.literaryNotes = { status: literaryNotesResult?.status };
    if (!touringNotesResult?.ok) sectionErrors.touringNotes = { status: touringNotesResult?.status };
    if (!paNotesResult?.ok) sectionErrors.paNotes = { status: paNotesResult?.status };
    if (!actsResult?.ok) sectionErrors.acts = { status: actsResult?.status };
    if (Object.keys(sectionErrors).length > 0) {
      this.setState({ sectionErrors });
    }

    // Original snapshot logic — reached whether or not Phase 2 had partial
    // failures. The project itself is a Case 1-cleared success body here.
    // For siblings, we coerce the snapshot to a safe empty array on failure
    // rather than storing the raw error envelope: downstream code
    // (changedListValidator, saveChanges .forEach, etc.) assumes these are
    // arrays, and would crash if we parked e.g. `{ error: 'oops' }` from a
    // 500 response in originals.roles.
    const project = projectResult.body;
    const roles = roleResult?.ok ? roleResult.body : null;
    const castings = castingsResult?.ok ? castingsResult.body : null;
    const { originals } = this.state;
    this._projectEntities.forEach(pe => {
      originals[pe] = project[pe] ? JSON.parse(JSON.stringify(project[pe])) : [];
    });
    originals.roles = Array.isArray(roles) ? JSON.parse(JSON.stringify(roles)) : [];
    originals.castings = Array.isArray(castings) ? JSON.parse(JSON.stringify(castings)) : [];
    this.setState({ originals: originals });
  }

  // User-triggered Retry handler for the inline SectionError fallback.
  // Re-runs the full setForm() rather than only the failing loader — chosen
  // for simplicity and so a transient outage that took down several endpoints
  // can recover with a single click. retryToken is incremented so child tabs
  // that run their own loaders (Trackings, IndieNotes) can key an effect off
  // it and re-fetch too.
  retryAll() {
    const { dispatch, projectId } = this.props;
    if (!projectId || this.state.isRetrying) return;
    this.setState(prev => ({ isRetrying: true, retryToken: prev.retryToken + 1 }));
    this.setForm(dispatch, projectId).finally(() => {
      this.setState({ isRetrying: false });
    });
  }

  onChange(item) {
    const { project, roles } = this.props;
    const { originals } = this.state;

    if (item === 'roles') {
      this.changedListValidator(this.rolesHaveChanges, roles, originals.roles, item);
    } else if (
      item === 'repAgreements' ||
      item === 'salesFinancing' ||
      item === 'networks' ||
      item === 'studios' ||
      item === 'productionCompanies' ||
      item === 'readBy'
    ) {
      const jsonComparitor = (list1, list2) => {
        const hasChanges = JSON.stringify(list1) !== JSON.stringify(list2);
        return hasChanges;
      };
      this.changedListValidator(jsonComparitor, project[item], originals[item], item);
    } else if (
      this._projectEntities.some(i => {
        return i === item;
      })
    ) {
      this.changedListValidator(this.arrayHasNameChanges, project[item], originals[item], item);
    } else {
      throw Error(`Unsupported change element: ${item}`);
    }
  }

  // eslint-disable-next-line no-undef
  changedListValidator = function(comparator, list1, list2, item) {
    const { changedItems } = this.state;
    const hasChanges = comparator(list1, list2);
    const knownChange = changedItems.some(i => {
      return i === item;
    });
    if (hasChanges && !knownChange) {
      changedItems.push(item);
      this.setState({ changedItems });
    } else if (!hasChanges && knownChange) {
      this.setState({
        changedItems: changedItems.filter(i => {
          return i !== item;
        }),
      });
    } else {
      //this is stupid, but it forces a for the child making the change
      this.setState({ lastChangeFrom: item });
    }
  };

  arrayHasNameChanges(array1, array2) {
    if ((!array1 && array2) || (array1 && !array2) || array1.length !== array2.length) {
      return true;
    }
    return array1.some((a, index) => {
      return (
        a.name.trim().toLowerCase() !== array2[index].name.trim().toLowerCase() ||
        (index > 0 && a.primary !== array2[index].primary)
      );
    });
  }

  // eslint-disable-next-line no-undef
  rolesHaveChanges = function(array1, array2) {
    if ((!array1 && array2) || (array1 && !array2)) {
      return true;
    }
    const actives1 = array1.filter(a => {
      return a.active;
    });
    const actives2 = array2.filter(a => {
      return a.active;
    });
    if (actives1.length !== actives2.length) {
      return true;
    }
    return actives1.some((a, index) => {
      return (
        a.status.trim().toLowerCase() !== actives2[index].status.trim().toLowerCase() ||
        (a.type ? a.type.trim().toLowerCase() : '') !==
          (actives2[index].type ? actives2[index].type.trim().toLowerCase() : '') ||
        (a.personId && !actives2[index].personId) ||
        (!a.personId && actives2[index].personId) ||
        (a.personId ? a.personId.name.trim().toLowerCase() : '') !==
          (actives2[index].personId ? actives2[index].personId.name.trim().toLowerCase() : '')
      );
    });
  };

  cancelAll() {
    const { changedItems, originals } = this.state;
    const { project, roles } = this.props;
    changedItems.forEach(ci => {
      if (ci === 'roles') {
        roles.splice(0, roles.length);
        JSON.parse(JSON.stringify(originals.roles)).forEach(r => {
          roles.push(r);
        });
      } else if (
        this._projectEntities.some(pe => {
          return pe === ci;
        })
      ) {
        project[ci] = JSON.parse(JSON.stringify(originals[ci]));
      }
    });
    this.setState({ changedItems: [] });
  }

  saveAll() {
    const { changedItems, originals } = this.state;
    const { dispatch, project, roles } = this.props;
    const projectUpdates = {};
    const projectUpdatedEntities = [];
    const updatePromises = [];
    changedItems.forEach(ci => {
      if (ci === 'roles') {
        const rolesCopy = JSON.parse(JSON.stringify(roles));
        const rolePromises = rolesCopy.map(r => {
          if (r.personId) {
            if (r.personId.name.trim() === '') {
              r.personId = null;
            } else {
              r.personId = r.personId._id;
            }
          }

          r.projectId = project._id;
          if (!r._id) {
            // Create the role
            r.projectId = project._id;
            return dispatch(createRole(r));
          } else {
            // Update the role
            return dispatch(updateRole(r._id, r));
          }
        });
        const roleUpdatePromise = Promise.all(rolePromises)
          .then(() => {
            return dispatch(projectLoadRoles(project._id));
          })
          .then(results => {
            const freshRoles = results.body;
            roles.splice(0, roles.length);
            freshRoles.forEach(r => {
              roles.push(r);
            });
            originals.roles = JSON.parse(JSON.stringify(roles));
          });
        updatePromises.push(roleUpdatePromise);
      } else if (
        this._projectEntities.some(pe => {
          return pe === ci;
        })
      ) {
        projectUpdatedEntities.push(ci);
        projectUpdates[ci] = project[ci];
      }
    });
    if (Object.keys(projectUpdates).length > 0) {
      const projectUpdatePromise = dispatch(updateProject(project._id, projectUpdates)).then(result => {
        const p = result.body;
        projectUpdatedEntities.forEach(pue => {
          project[pue] = p[pue];
          originals[pue] = JSON.parse(JSON.stringify(p[pue]));
        });
      });
      updatePromises.push(projectUpdatePromise);
    }

    return Promise.all(updatePromises).then(() => {
      this.setState({ originals, changedItems: [] });
      notify.show('Project Saved', 'custom', 2000, { background: '#000000', text: '#FFFFFF' });
    });
  }

  saveChanges(changes) {
    const { project, dispatch } = this.props;

    const actions = [];

    Object.keys(changes.creates || {}).forEach(type => {
      const action = creates[type];
      const payloads = changes.creates[type] || [];

      payloads.forEach(payload => {
        actions.push(action(payload));
      });
    });

    Object.keys(changes.updates || {}).forEach(type => {
      if (type === 'projectRole') {
        const roles = changes.updates['projectRole'] || {};
        Object.keys(roles).forEach(id => {
          if (id.includes('NEW_ROLE')) {
            dispatch(createRole(roles[id]));
          } else {
            dispatch(updateRole(id, roles[id]));
          }
        });
      } else {
        const action = updates[type];
        const entities = changes.updates[type] || {};

        Object.keys(entities).forEach(id => {
          actions.push(action(id, entities[id]));
        });
      }
    });

    actions.push(loadProject(project._id));

    return actions.reduce((p, action) => {
      return p.then(() => this.props.dispatch(action));
    }, Promise.resolve());
  }

  shareLinkInEmail(project) {
    const trackingLink = `${process.env.REACT_APP_PROJECTS_URL}/project/${project._id}/information`;
    const emailString = `mailto:?subject=${project.name}&body=${trackingLink}`;

    window.location.href = emailString;
  }

  handleDeleteProject() {
    const { dispatch, projectId } = this.props;
    dispatch(deleteProject(projectId)).then(() => {
      // after deleting the project, navigate back to the home screen
      window.location.href = defaultUrl;
    });
  }

  getFocusItem() {
    const { castings } = this.props;
    return castings && castings[this.state.focused];
  }

  print = (options = {}) => {
    const { project } = this.props;
    this.performDownload(
      'v2/print-report',
      options.saveAs || project.name,
      {
        options,
        projects: [project._id],
      },
      [{ printType: 'project_detail' }]
    );
  };

  downloadCSV = (options = {}) => {
    const { project } = this.props;
    this.performDownload(
      'v2/print-report-csv',
      options.saveAs || project.name,
      {
        options,
        projects: [project._id],
      },
      [{ printType: 'project_detail' }],
      'csv'
    );
  };

  // eslint-disable-next-line no-undef
  setIsDownloading = (url, isDownloading) => {
    this.setState({ isPrinting: isDownloading });
  };

  performDownload(url, fileName, options, query, fileExtension = 'pdf') {
    const { user, desk } = this.props;
    if (this.state.isPrinting) {
      return;
    }

    downloadFile(url, fileName, user, desk, this.setIsDownloading, options, query, undefined, fileExtension);
  }

  // eslint-disable-next-line no-undef
  renderSaveCancel = isDirty => {
    const { styles } = this.props;
    const onCancel = () => {
      this.cancelAll();
    };
    const onSave = () => {
      this.saveAll();
    };
    return isDirty ? (
      <div className={styles.headerEdit}>
        <div className={styles.headerButtons}>
          {isDirty && onCancel && (
            <EditButton
              type={buttonTypes.cancel}
              className={styles.icon}
              onClick={() => {
                onCancel();
              }}
            />
          )}
          {isDirty && onSave && (
            <EditButton
              type={buttonTypes.save}
              className={styles.icon}
              onClick={() => {
                onSave();
              }}
            />
          )}
        </div>
      </div>
    ) : (
      <div style={{ height: 25 }} />
    );
  };

  render() {
    const {
      history,
      dispatch,
      notes,
      project,
      projectId,
      styles,
      user,
      profileTags,
      projects,
      roles,
      repAgreements,
      salesFinancing,
      literaryNotes,
      talentNotes,
      paNotes,
      touringNotes,
      acts,
      desk,
      _hasAccess = hasAccess,
    } = this.props;
    const {
      isPrintManagerCollapsed,
      originals,
      changedItems,
      projectLoadError,
      sectionErrors,
      isRetrying,
      retryToken,
      isBannerDismissed,
      isLoadingProject,
    } = this.state;
    const castings = originals.castings;

    // Loading gate — covers the window between mount and Phase 1 completion.
    // Rendered BEFORE the Case 1 and `!project` branches so a stale redux
    // `projects.project` value from a prior visit can't briefly paint the
    // old chrome (and fire the old `project?.message` toast) during a
    // forward-button navigation back to a 404'd URL.
    if (isLoadingProject) {
      return (
        <div className={styles.container}>
          <div className={styles.spinner}>
            <Spinner />
          </div>
        </div>
      );
    }

    // Case 1 fallback — project itself couldn't be loaded (deleted / 404 /
    // 5xx). Rendered in place at /project/<id> so the URL is preserved; no
    // retry button per the agreed uniform UX, the user is nudged back home.
    if (projectLoadError) {
      const isNotFound = projectLoadError.kind === 'not_found';
      return (
        <ProjectNotFound
          code={isNotFound ? '404' : 'Error'}
          title={isNotFound ? 'Project not found' : "Couldn't load this project"}
          message={
            isNotFound
              ? 'This project may have been deleted, or the link is no longer valid.'
              : 'Something went wrong on our side while loading this project. Please head back home and try again.'
          }
        />
      );
    }

    // Defensive — Phase 1 said OK but redux hasn't populated yet. Rare, but
    // keeps the spinner behavior consistent with the pre-existing contract.
    if (!project) {
      return (
        <div className={styles.container}>
          <div className={styles.spinner}>
            <Spinner />
          </div>
        </div>
      );
    }
    const focused = this.getFocusItem();

    const viewProps = {
      project,
      roles,
      repAgreements,
      salesFinancing,
      profileTags,
      castings,
      dispatch,
      notes,
      focused,
      projects,
      literaryNotes,
      talentNotes,
      setFocused: focused => this.setState({ focused }),
      saveChanges: x => {
        this.setState({ isEdit: false });
        this.saveChanges(x);
        this.setForm(dispatch, projectId);
      },
      onCancel: () => {
        this.setState({ isEdit: false });
        this.setForm(dispatch, projectId);
      },
      originals,
      onChange: this.onChange,
      changedItems,
      handleDeleteProject: this.handleDeleteProject.bind(this),
      // Case 2 wiring — each tab reads its own key off sectionErrors (e.g.
      // sectionErrors.roles for Auspices) and renders <SectionError /> instead
      // of its data when present. onSectionRetry re-dispatches every loader,
      // and isRetrying/retryToken give tabs the state they need to drive the
      // "Retrying…" button label and (for self-fetching tabs) re-trigger
      // their own useEffect loaders.
      sectionErrors,
      onSectionRetry: this.retryAll,
      isRetrying,
      retryToken,
    };

    const isFairAndFestivals = project?.type === 'Fairs and Festivals';
    const canSeeIndieNotes =
      _hasAccess('details', 'indieNotes') && (project?.type === 'Feature' || project?.type === 'Unscripted');
    // (Removed the `project?.message` → notify.show('Unable to load…') toast.
    // That toast was redundant with the Case 1 fallback and, worse, caused
    // the "toast flash on forward-navigation" issue — it fired on first
    // render while the redux store still held the previous `{ message }`
    // envelope, moments before setForm Phase 1 flipped projectLoadError
    // and <ProjectNotFound /> took over.)
    return (
      <ExceptionHandler history={history}>
        <div className={classnames(styles.container, styles.pageScrollArea)}>
          <Helmet>
            <title>{(project && project.name) || 'PROJECT DETAIL'}</title>
          </Helmet>
          <div className={styles.header}>
            <div className={styles.content}>
              <Title
                {...viewProps}
                editing={this.state.isEdit}
                setEdit={isEdit => {
                  this.setState({ isEdit });
                }}
                user={user}
              />
              <div className={classnames(styles.menu, 'projectMenu')}>
                {isFairAndFestivals ? (
                  <FairsAndFestivalsMenu styles={styles} project={project} />
                ) : (
                  <DefaultProjectMenu styles={styles} project={project} canSeeIndieNotes={canSeeIndieNotes} />
                )}
                <div className={classnames(styles.actionButtons, 'actionButtons')}>
                  <button
                    className={styles.printButton}
                    hidden={!isPrintManagerCollapsed}
                    onClick={() => this.setState({ isPrintManagerCollapsed: false })}
                    data-cy={cypressTags.PROJECT_ACTIONS.TOGGLE_PRINT_BUTTON}
                  >
                    <i className={classnames('fa fa-fw fa-print', 'printButton')} />
                    Print
                  </button>
                  <IconButton
                    className={classnames(styles.shareButton, 'shareButton')}
                    toggle={true}
                    onClick={() => this.shareLinkInEmail(project)}
                    trueText="Share"
                    trueIcon="fa-share-alt"
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Page-level signal that at least one sibling endpoint failed
              (Case 2). Intentionally generic — the per-tab SectionError
              cards carry the "which tab" detail. Dismissible per-load; a
              new setForm() with fresh failures surfaces it again.
              Wrapped in styles.content so the banner shares the same
              sizes.container max-width as the title row and tab cards
              instead of stretching edge-to-edge. */}
          {!isBannerDismissed && Object.keys(sectionErrors).length > 0 && (
            <div className={styles.content}>
              <SectionErrorBanner
                onRetry={this.retryAll}
                onDismiss={() => this.setState({ isBannerDismissed: true })}
                isRetrying={isRetrying}
              />
            </div>
          )}
          {this.renderSaveCancel(changedItems.length > 0)}
          {!isPrintManagerCollapsed && (
            <PrintManager
              className={styles.printDrawer}
              collapseEvent={() => this.setState({ isPrintManagerCollapsed: true })}
              print={this.print.bind(this)}
              userInTalent={user && user.personId && user.personId.department === 'Talent'}
              downloadCSV={this.downloadCSV.bind(this)}
              showCSVUi={_hasAccess('printing', 'csvExport')}
            />
          )}
          <div className={styles.body}>
            <div className={styles.content}>
              {isFairAndFestivals ? (
                <FairsAndFestivalsRoutes
                  acts={acts}
                  viewProps={viewProps}
                  project={project}
                  touringNotes={touringNotes}
                />
              ) : (
                <DefaultRoutes
                  literaryNotes={literaryNotes}
                  viewProps={viewProps}
                  project={project}
                  talentNotes={talentNotes}
                  paNotes={paNotes}
                  desk={desk}
                  user={user}
                  canSeeIndieNotes={canSeeIndieNotes}
                  dispatch={dispatch}
                />
              )}
            </div>
          </div>
        </div>
      </ExceptionHandler>
    );
  }
}

const withStyles = styled({
  container: {
    minWidth: 800,
  },
  header: {
    background: colors.contentBackground,
  },
  headerEdit: {
    background: '#90E2D3',
    display: 'flex',
  },
  headerButtons: {
    marginRight: '50px',
    marginLeft: 'auto',
    display: 'flex',
  },
  icon: {
    marginLeft: '10px',
  },
  spinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 'calc(100vh - 48px)',
  },
  content: helpers.merge(sizes.container, {
    display: 'flex',
    flexDirection: 'column',
  }),
  menu: helpers.merge(elements.menu, {
    marginBottom: '5px',
  }),
  menuItem: elements.menuItem,
  activeItem: elements.activeMenuItem,
  actionButtons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginLeft: 'auto',
  },
  pageScrollArea: {
    marginBottom: '200px',
  },
  printButton: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    lineHeight: 1,
    marginLeft: '10px',
    padding: '5px 0',
    textTransform: 'uppercase',
    border: 'none',
    backgroundColor: 'unset',
    '&:focus': {
      outline: 'none',
    },
  },
});

const mapStateToProps = (store, ownProps) => {
  const projectId = ownProps.match.params.projectId;
  const query = new URLSearchParams(ownProps.location.search);
  const { user } = store;
  const { project } = store.projects;
  const { projectNotes } = store.projectNotes;
  const { profileTags, roles, repAgreements, salesFinancing } = store.projects;
  const { literaryNotes, touringNotes, talentNotes, paNotes, acts } = store.projectNotes;

  // Defensive coercion for slices that downstream code treats as arrays.
  // The library's reducers (project-notes, projects, project-castings) blindly
  // write `action.body` on load — on a 500 that body is a `{ error: '...' }`
  // envelope, not an array. Every consumer that calls `.filter()` / `.map()`
  // / `.sort()` / `.reduce()` on these slices would then crash with
  // "X.filter is not a function". Coercing here is one line and protects
  // every downstream reader (Information, Title, Auspices, Activity, etc.)
  // without having to defend each call site individually.
  const safeArray = v => (Array.isArray(v) ? v : []);

  return {
    user,
    projectId,
    project,
    notes: projectNotes,
    roles: safeArray(roles),
    repAgreements,
    salesFinancing,
    castings: store && store.castingSearch && store.castingSearch.castings,
    profileTags: safeArray(profileTags),
    literaryNotes: safeArray(literaryNotes),
    touringNotes: safeArray(touringNotes),
    talentNotes: safeArray(talentNotes),
    paNotes: safeArray(paNotes),
    acts: safeArray(acts),
    errorMsg: query.get('errorMsg'),
  };
};

export default withLDConsumer()(connect(mapStateToProps)(withStyles(Project)));
